import { type ReactNode, useMemo } from 'react';

interface UseSlidesArguments {
  activeIndex: number;
  render: (slidePosition: 'left' | 'center' | 'right', index?: number) => ReactNode;
  shouldLoop: boolean;
  totalSlides: number;
}

/**
 * A hook that takes information about the active slide in a photo carousel
 * and returns an array that contains the active slide, the previous slide,
 * and the next slide.
 *
 * @example
 * const content = useSlidesRange({
 *   activeIndex,
 *   render,
 *   shouldLoop,
 *   totalSlides,
 * });
 */
export const useSlidesRange = (args: UseSlidesArguments) => {
  const { activeIndex, render, shouldLoop, totalSlides } = args;

  /*
   * Memoize on the destructured primitives + render callback rather than
   * on the `args` object — callers invoke this with an inline object
   * literal, so depending on `[args]` would invalidate the memo on every
   * render and produce a fresh `Array<ReactNode>` reference.
   */
  return useMemo(() => {
    const items = [];

    const getNextSlideIndex = (index: number) => {
      if (index >= totalSlides - 1) {
        return shouldLoop ? 0 : undefined;
      }

      return index + 1;
    };

    const getPreviousSlideIndex = (index: number) => {
      if (index <= 0) {
        return shouldLoop ? totalSlides - 1 : undefined;
      }

      return index - 1;
    };

    // 1. Previous slide
    items.push(render('left', getPreviousSlideIndex(activeIndex)));

    // 2. Active slide
    items.push(render('center', activeIndex));

    // 3. Next slide
    items.push(render('right', getNextSlideIndex(activeIndex)));

    return items;
  }, [activeIndex, render, shouldLoop, totalSlides]);
};
